function isAuthenticated() {
    return !!localStorage.getItem('authToken');
}

function getPlanType(store) {
    return store?.state?.planType || 'basic';
}

function isPlanBasic(store) {
    return getPlanType(store) === 'basic';
}

function hasModule(store, moduleName) {
    if (!store || !store.state || !store.state.modules) {
        return false;
    }
    return store.state.modules.includes(moduleName);
}

function hasMyPos(store) {
    if (!store || !store.state) {
        return false;
    }
    return store.state.hasMyPos || false;
}

function hasActiveFilter(route) {
    if (!route || !route.query) return false;

    const filterKeys = ['driver', 'car', 'concession', 'from', 'to'];
    return filterKeys.some((key) => route.query[key]);
}

function createCurrentFilterCommand(route, store) {
    const { path, query } = route;
    const filters = [];

    let primaryEntity = null;

    if (query.driver) {
        const driverName = getDriverName(query.driver, store);
        filters.push(`Fahrer: ${driverName}`);
        primaryEntity = { type: 'driver', id: query.driver, name: driverName };
    }
    if (query.car) {
        const carName = getCarName(query.car, store);
        filters.push(`Fahrzeug: ${carName}`);
        if (!primaryEntity) primaryEntity = { type: 'car', id: query.car, name: carName };
    }
    if (query.concession) {
        const concessionName = getConcessionName(query.concession, store);
        filters.push(`Konzession: ${concessionName}`);
        if (!primaryEntity)
            primaryEntity = { type: 'concession', id: query.concession, name: concessionName };
    }
    if (query.from && query.to) {
        filters.push(`Zeitraum: ${query.from} - ${query.to}`);
    } else if (query.from) {
        filters.push(`Von: ${query.from}`);
    } else if (query.to) {
        filters.push(`Bis: ${query.to}`);
    }

    if (filters.length === 0) return null;

    const filterText = filters.join(', ');
    const pageName = getPageNameFromPath(path);

    return {
        id: 'current-filter',
        title: `Aktuelle Filter: ${filterText}`,
        subtitle: `${pageName} - Details anzeigen`,
        category: 'Aktuelle Ansicht',
        icon: 'ri-filter-3-line',
        keywords: ['filter', 'aktuell', 'aktiv', 'current', 'anzeigen', 'details'],
        visible: () => true,
        enabled: () => true,
        primaryEntity,
        action: (context) => {
            if (primaryEntity) {
                return {
                    type: 'open-entity-modal',
                    entityType: primaryEntity.type,
                    entityId: primaryEntity.id,
                    entityName: primaryEntity.name,
                };
            }
        },
    };
}

function getDriverName(driverId, store) {
    if (!store || !store.state) return driverId;

    const drivers = store.state.drivers || store.state.employees || [];

    if (Array.isArray(drivers)) {
        const driver = drivers.find(
            (d) => d.id == driverId || d.driverNumber == driverId || d.driverId == driverId,
        );
        if (driver) {
            const name = driver.name || driver.value || driver.label || '';
            const number = driver.driverNumber || driver.id || driverId;
            return name ? `${name} (${number})` : number;
        }
    }

    return driverId;
}

function getCarName(carId, store) {
    if (!store || !store.state) return carId;

    const cars = store.state.cars || [];

    if (Array.isArray(cars)) {
        const car = cars.find((c) => c.id == carId || c.licenseNumber == carId || c.carId == carId);
        if (car) {
            const name = car.licenseNumber || car.name || car.value || car.label || '';
            return name || carId;
        }
    }

    return carId;
}

function getConcessionName(concessionId, store) {
    if (!store || !store.state) return concessionId;

    const concessions = store.state.concessions || [];

    if (Array.isArray(concessions)) {
        const concession = concessions.find(
            (c) => c.id == concessionId || c.concessionId == concessionId,
        );
        if (concession) {
            const name = concession.name || concession.value || concession.label || '';
            return name || concessionId;
        }
    }

    return concessionId;
}

function getPageNameFromPath(path) {
    const pathMap = {
        '/dashboard': 'Dashboard',
        '/revenue': 'Umsatz',
        '/analytics': 'Analyse',
        '/shifts': 'Schichten',
        '/trips': 'Einzelfahrten',
        '/working-time': 'Arbeitszeiten',
        '/card-payments': 'Kartenzahlungen',
        '/cars': 'Fahrzeuge',
        '/employees': 'Mitarbeiter',
        '/operationride': 'Betriebsnachweis',
    };

    return pathMap[path] || 'Aktuelle Seite';
}

function createNavCommand(id, title, path, options = {}) {
    return {
        id,
        title,
        subtitle: options.subtitle || '',
        category: options.category || 'Navigation',
        icon: options.icon || 'ri-arrow-right-line',
        shortcut: options.shortcut || null,
        keywords: options.keywords || [],
        visible: options.visible || (() => true),
        enabled: options.enabled || (() => true),
        action: (context) => {
            const query = options.query || {};
            if (options.preserveQuery) {
                Object.assign(query, context.route?.query || {});
            }
            context.router.push({ path, query });
        },
    };
}

function createActionCommand(id, title, handler, options = {}) {
    return {
        id,
        title,
        subtitle: options.subtitle || '',
        category: options.category || 'Aktion',
        icon: options.icon || 'ri-add-line',
        shortcut: options.shortcut || null,
        keywords: options.keywords || [],
        visible: options.visible || (() => true),
        enabled: options.enabled || (() => true),
        action: handler,
    };
}

export function getAllCommands(context = {}) {
    const { router, store, route } = context;
    const authenticated = isAuthenticated();
    const planBasic = isPlanBasic(store);

    const commands = [];

    if (authenticated && route && hasActiveFilter(route)) {
        const filterCommand = createCurrentFilterCommand(route, store);
        if (filterCommand) {
            commands.push(filterCommand);
        }
    }

    if (authenticated) {
        commands.push(
            createNavCommand('nav-overview', 'Dashboard', '/dashboard', {
                icon: 'ri-dashboard-line',
                keywords: ['dashboard', 'übersicht', 'start', 'home'],
                subtitle: 'Zur Übersicht navigieren',
                visible: () => hasModule(store, 'dashboard'),
            }),
            createNavCommand('nav-revenue', 'Umsatz', '/revenue', {
                icon: 'ri-money-euro-circle-line',
                keywords: ['umsatz', 'revenue', 'einnahmen', 'money', 'geld'],
                subtitle: 'Umsatz anzeigen',
                visible: () => hasModule(store, 'revenue') && !planBasic,
            }),
            createNavCommand('nav-analytics', 'Analyse', '/analytics', {
                icon: 'ri-bar-chart-line',
                keywords: ['analyse', 'analytics', 'statistiken', 'stats', 'statistik'],
                subtitle: 'Zur Analyse navigieren',
                visible: () => hasModule(store, 'performanceAnalysis'),
            }),
            createNavCommand('nav-trips', 'Einzelfahrten', '/trips', {
                icon: 'ri-steering-2-line',
                keywords: ['einzelfahrten', 'trips', 'fahrten', 'rides'],
                subtitle: 'Einzelfahrten anzeigen',
            }),
            createNavCommand('nav-shifts', 'Schichten', '/shifts', {
                icon: 'ri-repeat-line',
                keywords: ['schichten', 'shifts', 'dienste'],
                subtitle: 'Schichten anzeigen',
            }),
            createNavCommand('nav-working-time', 'Arbeitszeiten', '/working-time', {
                icon: 'ri-time-line',
                keywords: ['arbeitszeiten', 'working time', 'arbeitszeit', 'hours'],
                subtitle: 'Arbeitszeiten anzeigen',
                visible: () => hasModule(store, 'workTime'),
            }),
            createNavCommand('nav-employees', 'Mitarbeiter', '/employees', {
                icon: 'ri-team-line',
                keywords: ['mitarbeiter', 'employees', 'drivers', 'fahrer'],
                subtitle: 'Mitarbeiter anzeigen',
            }),
            createNavCommand('nav-cars', 'Fahrzeuge', '/cars', {
                icon: 'ri-car-line',
                keywords: ['fahrzeuge', 'cars', 'vehicles', 'autos', 'fahrzeug'],
                subtitle: 'Fahrzeuge anzeigen',
            }),
            createNavCommand('nav-operation-ride', 'Betriebsnachweise', '/operationride', {
                icon: 'ri-file-list-line',
                keywords: ['betriebsnachweise', 'operation ride', 'betriebsnachweis', 'operation'],
                subtitle: 'Betriebsnachweise anzeigen',
                visible: () => hasModule(store, 'operationRecords'),
            }),
            createNavCommand('nav-invoice', 'Rechnungen', '/invoice', {
                icon: 'ri-file-text-line',
                keywords: ['rechnungen', 'invoice', 'rechnung', 'invoices'],
                subtitle: 'Rechnungen anzeigen',
            }),
            createNavCommand('nav-drivers-log', 'Fahrtenbuch', '/driverslog', {
                icon: 'ri-file-list-3-line',
                keywords: ['fahrtenbuch', 'drivers log', 'log', 'buch'],
                subtitle: 'Fahrtenbuch anzeigen',
                visible: () => hasModule(store, 'driverLogs'),
            }),
            createNavCommand('nav-scheduler', 'Schichtplanner', '/schedular', {
                icon: 'ri-timeline-view',
                keywords: [
                    'schichtplanner',
                    'scheduler',
                    'kalender',
                    'schedule',
                    'planning',
                    'planer',
                ],
                subtitle: 'Schichtplanner öffnen',
                visible: () => hasModule(store, 'scheduler'),
            }),
            createNavCommand('nav-calendar', 'Kalender', '/calendar', {
                icon: 'ri-calendar-event-line',
                keywords: ['kalender', 'calendar', 'termin', 'termine'],
                subtitle: 'Kalender anzeigen',
                visible: () => hasModule(store, 'calendar'),
            }),
            createNavCommand('nav-card-payments', 'Kartenzahlungen', '/card-payments', {
                icon: 'ri-bank-card-line',
                keywords: ['kartenzahlungen', 'card payments', 'payments', 'zahlungen'],
                subtitle: 'Kartenzahlungen anzeigen',
                visible: () => hasMyPos(store),
            }),
            createNavCommand('nav-savings-calculator', 'Rücklagenrechner', '/savings-calculator', {
                icon: 'ri-calculator-line',
                keywords: [
                    'rücklagenrechner',
                    'savings',
                    'calculator',
                    'sparrechner',
                    'berechnung',
                ],
                subtitle: 'Rücklagenrechner öffnen',
                visible: () => hasModule(store, 'savingsCalculator'),
            }),
            createNavCommand('nav-settings', 'Einstellungen', '/settings', {
                icon: 'ri-settings-line',
                keywords: ['einstellungen', 'settings', 'config', 'preferences', 'konfiguration'],
                subtitle: 'Einstellungen öffnen',
            }),
            createNavCommand('nav-changelog', 'Changelog', '/changelog', {
                icon: 'ri-file-list-line',
                keywords: ['changelog', 'updates', 'news', 'neuigkeiten', 'änderungen'],
                subtitle: 'Neueste Updates anzeigen',
            }),
            createNavCommand('nav-faq', 'FAQ', '/faq', {
                icon: 'ri-question-line',
                keywords: ['faq', 'help', 'hilfe', 'questions', 'fragen'],
                subtitle: 'Häufig gestellte Fragen',
            }),
        );
    }

    commands.push(
        createNavCommand('nav-home', 'Startseite', '/', {
            icon: 'ri-home-line',
            keywords: ['startseite', 'home', 'start', 'homepage'],
            subtitle: 'Zur Startseite',
            visible: () => !authenticated,
        }),
        createNavCommand('nav-features', 'Funktionen', '/features', {
            icon: 'ri-star-line',
            keywords: ['funktionen', 'features', 'eigenschaften'],
            subtitle: 'Funktionen anzeigen',
        }),
        createNavCommand('nav-pricing', 'Preise', '/pricing', {
            icon: 'ri-price-tag-line',
            keywords: ['preise', 'pricing', 'price', 'preis'],
            subtitle: 'Preise anzeigen',
        }),
        createNavCommand('nav-contact', 'Kontakt', '/contact', {
            icon: 'ri-mail-line',
            keywords: ['kontakt', 'contact', 'support'],
            subtitle: 'Kontakt aufnehmen',
        }),
        createNavCommand('nav-demo', 'Demo anfordern', '/demo', {
            icon: 'ri-video-line',
            keywords: ['demo', 'test', 'trial', 'anfordern'],
            subtitle: 'Demo anfordern',
        }),
        createNavCommand('nav-blog', 'Blog', '/blog', {
            icon: 'ri-article-line',
            keywords: ['blog', 'news', 'articles', 'artikel', 'neuigkeiten'],
            subtitle: 'Blog anzeigen',
        }),
        createNavCommand('nav-tse-checklist', 'TSE Checkliste', '/tse-checklist', {
            icon: 'ri-checkbox-line',
            keywords: ['tse', 'checklist', 'checkliste', 'compliance'],
            subtitle: 'TSE Checkliste anzeigen',
        }),
    );

    if (authenticated) {
        commands.push(
            createNavCommand('help-faq', 'FAQ', '/faq', {
                icon: 'ri-question-line',
                category: 'Hilfe',
                keywords: ['faq', 'help', 'hilfe', 'fragen'],
                subtitle: 'Häufig gestellte Fragen',
            }),
            createNavCommand('help-changelog', 'Changelog', '/changelog', {
                icon: 'ri-file-list-line',
                category: 'Hilfe',
                keywords: ['changelog', 'updates', 'änderungen', 'neuigkeiten'],
                subtitle: 'Neueste Updates',
            }),
        );
    }

    return commands.filter((cmd) => {
        try {
            return cmd.visible({ router, store, route }) && cmd.enabled({ router, store, route });
        } catch (e) {
            return false;
        }
    });
}

export function formatCommandForNinjaKeys(command, context = {}) {
    const iconHtml = command.icon ? `<i class="${command.icon}"></i>` : '';

    const keywordsStr = Array.isArray(command.keywords)
        ? command.keywords.join(' ')
        : command.keywords || '';

    return {
        id: command.id,
        title: command.title,
        subtitle: command.subtitle,
        section: command.category,
        keywords: keywordsStr,
        icon: iconHtml,
        hotkey: command.shortcut || '',
        primaryEntity: command.primaryEntity,
        entityType: command.entityType,
        entityId: command.entityId,
        entityName: command.entityName,
        entityData: command.entityData,
        handler: () => {
            if (command.action) {
                command.action(context);
            }
        },
    };
}

export function getCommandsForNinjaKeys(context = {}) {
    const commands = getAllCommands(context);
    return commands.map((cmd) => formatCommandForNinjaKeys(cmd, context));
}
